import { Injectable } from '@angular/core';
import { GetterService } from 'src/app/service/getter-service.service';

/**
 * Marketing campaign attribution.
 *
 * Reads the UTM parameters off the URL the visitor landed on
 * (`?utm_source=instagram&utm_medium=social&utm_campaign=oct-biryani`) and
 * keeps them in localStorage (brand-scoped, via GetterService) so the
 * checkout can send them with the order — the customer rarely orders on the
 * same page view they landed on, and the SPA router drops the query string
 * on the first navigation.
 *
 * Rule: last-touch, 30 days. A new landing that carries UTM parameters
 * replaces the stored one; nothing is stored for landings without them.
 *
 * `captureFromUrl()` must run before any flavour / host redirect rewrites
 * the URL, so AppComponent calls it in its constructor.
 */
export interface Attribution {
  UtmSource: string | null;
  UtmMedium: string | null;
  UtmCampaign: string | null;
  UtmContent: string | null;
  /** gclid / fbclid / ttclid / msclkid, whichever came first */
  ClickId: string | null;
  /** ISO 8601 UTC time of the landing */
  LandedAt: string;
}

const STORAGE_KEY = 'campaignAttribution';
const WINDOW_DAYS = 30;
const CLICK_ID_PARAMS = ['gclid', 'fbclid', 'ttclid', 'msclkid'];
const MAX_LENGTH = 150;

@Injectable({
  providedIn: 'root',
})
export class AttributionService {
  constructor(private local: GetterService) {}

  /** Store the UTM parameters of the current URL, when there are any. */
  captureFromUrl(): void {
    let params: URLSearchParams;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }

    const utmSource = this.clean(params.get('utm_source'));
    const utmMedium = this.clean(params.get('utm_medium'));
    const utmCampaign = this.clean(params.get('utm_campaign'));
    const utmContent = this.clean(params.get('utm_content'));
    const clickId = this.clean(
      CLICK_ID_PARAMS.map((p) => params.get(p)).find((v) => !!v) ?? null,
      200
    );

    if (!utmSource && !utmMedium && !utmCampaign && !utmContent && !clickId) {
      return;
    }

    const attribution: Attribution = {
      UtmSource: utmSource,
      UtmMedium: utmMedium,
      UtmCampaign: utmCampaign,
      UtmContent: utmContent,
      ClickId: clickId,
      LandedAt: new Date().toISOString(),
    };

    try {
      this.local.set(STORAGE_KEY, JSON.stringify(attribution));
    } catch {
      /* storage unavailable (private mode / quota) — the order is still placed, just unattributed */
    }
  }

  /** The stored attribution, or null when there is none or it has expired. */
  get(): Attribution | null {
    let raw: string | null;
    try {
      raw = this.local.get(STORAGE_KEY);
    } catch {
      return null;
    }
    if (!raw) return null;

    let attribution: Attribution;
    try {
      attribution = JSON.parse(raw);
    } catch {
      this.clear();
      return null;
    }

    const landedAt = Date.parse(attribution?.LandedAt);
    const ageMs = Date.now() - landedAt;
    if (isNaN(landedAt) || ageMs > WINDOW_DAYS * 24 * 60 * 60 * 1000) {
      this.clear();
      return null;
    }

    return attribution;
  }

  clear(): void {
    try {
      this.local.remove(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  private clean(value: string | null, maxLength = MAX_LENGTH): string | null {
    if (!value) return null;
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return null;
    return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
  }
}
